import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, MapPin, Clock, Route, ArrowLeft, ArrowRight, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import GoogleMapComponent from '../components/GoogleMap';
import mockData from '../mock';

const RouteMap = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [calculatedRoute, setCalculatedRoute] = useState(null);
  
  const { 
    selectedDate, 
    hourlyRate, 
    propertyType, 
    propertySize, 
    floorType, 
    crewRecommendation,
    originAddress,
    destinationAddress,
    addressType
  } = location.state || {};

  // Use calculated route data or fallback to mock data
  const routeData = calculatedRoute || mockData.mockRouteData;

  const handleRouteCalculated = (routeInfo) => {
    setCalculatedRoute(routeInfo);
  };

  const handleConfirmRoute = () => {
    navigate('/additional-services', {
      state: {
        selectedDate,
        hourlyRate,
        propertyType,
        propertySize,
        floorType,
        crewRecommendation,
        originAddress,
        destinationAddress,
        routeData: calculatedRoute || mockData.mockRouteData
      }
    });
  };

  const handleGoBack = () => {
    navigate('/address-input', { 
      state: { 
        selectedDate, 
        hourlyRate, 
        propertyType, 
        propertySize, 
        floorType, 
        crewRecommendation 
      },
      replace: true
    });
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#F8F7F4'}}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 cursor-pointer group">
              <img 
                src="https://customer-assets.emergentagent.com/job_swiftmover/artifacts/7o75lsp7_Emisa%20Movers%20Logo.jpeg" 
                alt="Emisa Movers Logo"
                className="h-28 w-28 transition-all duration-300 transform group-hover:scale-110 group-hover:shadow-2xl group-active:scale-105 rounded-lg"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Emisa Movers</h1>
                <p className="text-xs text-amber-700 font-medium">GETS IT DONE ON TIME</p>
              </div>
            </a>
            <div className="flex items-center gap-3">
              <a 
                href="tel:7134879097"
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 hover:shadow-2xl hover:scale-110 text-white px-4 py-2 rounded-lg font-medium shadow-md transition-all duration-300 transform"
              >
                <Phone className="h-4 w-4" />
                (713) 487-9097
              </a>
              <a href="/" onClick={(e) => { e.preventDefault(); window.location.href = '/#book-now'; }} className="bg-teal-600 hover:bg-teal-700 hover:shadow-2xl hover:scale-110 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 transform">Book It NOW</a>
              <Button
                variant="outline"
                onClick={handleGoBack}
                className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white border-gray-600"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Addresses
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Booking Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-2 mb-3 shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <div>
              <span className="text-gray-600">Date:</span>
              <div className="font-medium text-teal-600 text-xs">{selectedDate}</div>
            </div>
            <div>
              <span className="text-gray-600">Rate:</span>
              <div className="font-medium text-red-600 text-xs">${hourlyRate}/h</div>
            </div>
            <div>
              <span className="text-gray-600">Property:</span>
              <div className="font-medium text-teal-600 text-xs capitalize">{propertyType}</div>
            </div>
            <div>
              <span className="text-gray-600">Size:</span>
              <div className="font-medium text-teal-600 text-xs">{propertySize} sqft</div>
            </div>
            <div>
              <span className="text-gray-600">Crew:</span>
              <div className="font-medium text-teal-600 text-xs">{crewRecommendation?.men} movers</div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Route Overview
          </h1>
          <p className="text-sm sm:text-base text-gray-700">
            Review your moving route and distance
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-4 mb-4">
          {/* Left Side - All Route Information */}
          <div className="space-y-3">
            {/* Locations */}
            <Card>
              <CardHeader className="p-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MapPin className="h-4 w-4 text-teal-600" />
                  Locations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 p-3 pt-0">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-teal-600 rounded-full mt-1"></div>
                  <div>
                    <div className="font-medium text-xs text-gray-600">FROM</div>
                    <div className="text-xs text-gray-700">{originAddress}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 pl-1">
                  <div className="w-0.5 h-6 bg-gray-300"></div>
                </div>
                
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-teal-600 rounded-full mt-1"></div>
                  <div>
                    <div className="font-medium text-xs text-gray-600">TO</div>
                    <div className="text-xs text-gray-700">{destinationAddress}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Route Info */}
            <Card>
              <CardHeader className="p-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Route className="h-4 w-4 text-teal-600" />
                  Route Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Distance:</span>
                    <span className="font-medium text-teal-600 text-xs">{routeData.distance}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Travel Time:</span>
                    <span className="font-medium text-teal-600 text-xs">{routeData.duration}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Route Type:</span>
                    <span className="font-medium text-teal-600 text-xs">Optimal</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Travel Considerations */}
            <Card className="bg-teal-50 border-teal-200">
              <CardHeader className="p-3">
                <CardTitle className="flex items-center gap-2 text-teal-600 text-base">
                  <Clock className="h-4 w-4" />
                  Travel Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <ul className="text-xs text-gray-700 space-y-0.5">
                  <li>• Travel time may vary with traffic</li>
                  <li>• Multiple trips may be required</li>
                  <li>• Fuel cost will be included in the travel fee</li>
                  <li>• Travel time displayed is for a normal car; truck generally drives slower and may take longer.</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Google Maps */}
          <div>
            <Card>
              <CardHeader className="p-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MapPin className="h-4 w-4 text-teal-600" />
                  Route Map
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <GoogleMapComponent 
                  originAddress={originAddress}
                  destinationAddress={destinationAddress}
                  onRouteCalculated={handleRouteCalculated}
                />
                
                <div className="mt-2 text-xs text-gray-600 text-center">
                  <p>Real-time route calculation powered by Google Maps</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Confirm Route Button */}
        <div className="text-center">
          <Card className="bg-white border border-gray-200 max-w-md mx-auto shadow-sm">
            <CardContent className="p-4">
              <h3 className="text-base font-semibold text-teal-600 mb-1">
                Route Confirmed
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                {routeData.distance} • {routeData.duration} travel time
              </p>
              <Button
                onClick={handleConfirmRoute}
                size="lg"
                className="bg-red-600 hover:bg-red-700 hover:shadow-xl hover:-translate-y-1 hover:scale-105 text-white px-6 py-2 text-base transition-all duration-300 transform"
              >
                Confirm Route & Continue
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RouteMap;